import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, UserCog } from "lucide-react";
import { Button } from "@nosible/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Input } from "@nosible/shared/components/ui/input";
import { Label } from "@nosible/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nosible/shared/components/ui/select";
import { trpc } from "@/admin/lib/trpc";

export function AdminUserEdit() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"user" | "admin">("user");

  const userQuery = trpc.admin.getUser.useQuery(
    { userId: userId! },
    { enabled: !!userId },
  );

  useEffect(() => {
    if (!userQuery.data?.user) return;
    setName(userQuery.data.user.name);
    setEmail(userQuery.data.user.email);
    setRole(userQuery.data.user.role === "admin" ? "admin" : "user");
  }, [userQuery.data]);

  const updateMutation = trpc.admin.updateUser.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.admin.getUser.invalidate({ userId: userId! }),
        utils.admin.listUsers.invalidate(),
      ]);
      navigate(`/users/${userId}`);
    },
  });

  if (!userId) {
    return <div className="admin-form-page">Missing user ID.</div>;
  }

  if (userQuery.isLoading) {
    return <div className="admin-form-page">Loading user…</div>;
  }

  if (userQuery.error || !userQuery.data?.user) {
    return (
      <div className="admin-form-page">
        Unable to load user: {userQuery.error?.message ?? "User not found"}
      </div>
    );
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !email.trim()) return;
    updateMutation.mutate({
      userId,
      name: name.trim(),
      email: email.trim(),
      role,
    });
  };

  return (
    <div className="admin-form-page">
      <Button variant="ghost" size="sm" asChild>
        <Link to={`/users/${userId}`}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to user
        </Link>
      </Button>

      <div className="admin-form-page__heading">
        <UserCog aria-hidden="true" />
        <div>
          <span>USERS / UPDATE</span>
          <h1>Update user</h1>
          <p>Change the account name, email address, or system role.</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Account details</CardTitle>
          <CardDescription>
            Changes take effect immediately. Administrator-role changes require
            extra care.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="admin-entity-form" onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="user-name">Name</Label>
              <Input
                id="user-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={updateMutation.isPending}
                required
              />
            </div>

            <div>
              <Label htmlFor="user-email">Email address</Label>
              <Input
                id="user-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={updateMutation.isPending}
                required
              />
            </div>

            <div>
              <Label htmlFor="user-role">System role</Label>
              <Select
                value={role}
                onValueChange={(value) => setRole(value as "user" | "admin")}
                disabled={updateMutation.isPending}
              >
                <SelectTrigger id="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {updateMutation.error && (
              <p className="admin-form-error">{updateMutation.error.message}</p>
            )}

            <div className="admin-entity-form__actions">
              <Button variant="outline" asChild>
                <Link to={`/users/${userId}`}>Cancel</Link>
              </Button>
              <Button
                className="brand-primary-button"
                type="submit"
                disabled={
                  updateMutation.isPending || !name.trim() || !email.trim()
                }
              >
                {updateMutation.isPending ? "Saving…" : "Save user"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
